import { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AppShell } from '@/components/layout/AppShell';
import { api, parseErr } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import {
  ClipboardCheck, ClipboardList, Wallet, AlertTriangle, Clock, PhoneCall, IndianRupee,
  Plus, Filter, CheckCircle2, XCircle,
} from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { useAutoRefetch } from '@/lib/useAutoRefetch';
import { fmtDate } from '@/lib/format';
import { SupportingEvidence } from '@/components/followups/SupportingEvidence';

const TABS = [
  { id: 'actions',     label: 'Internal Actions',  icon: ClipboardList },
  { id: 'approvals',   label: 'Client Approvals',  icon: ClipboardCheck },
  { id: 'collections', label: 'Collections',       icon: Wallet },
];

const STATUS_LABELS = {
  actions: {
    pending: 'Pending', in_progress: 'In progress', done: 'Done',
    blocked: 'Blocked', waiting_for_client: 'Waiting client', need_help: 'Need help',
  },
  approvals: {
    awaiting: 'Awaiting', approved: 'Approved', changes_requested: 'Changes requested',
    rejected: 'Rejected', payment_promised: 'Payment promised',
    payment_received: 'Payment received', no_response: 'No response',
  },
  collections: {
    not_sent: 'Not sent', sent: 'Sent', promised: 'Promised',
    partially_paid: 'Partial', paid: 'Paid', overdue: 'Overdue',
  },
};

const STATUS_TINT = {
  pending: 'chip',
  in_progress: 'chip chip-info',
  done: 'chip chip-success',
  blocked: 'chip chip-danger',
  waiting_for_client: 'chip chip-warning',
  need_help: 'chip chip-warning',
  awaiting: 'chip chip-info',
  approved: 'chip chip-success',
  changes_requested: 'chip chip-warning',
  rejected: 'chip chip-danger',
  payment_promised: 'chip chip-info',
  payment_received: 'chip chip-success',
  no_response: 'chip chip-warning',
  not_sent: 'chip',
  sent: 'chip chip-info',
  promised: 'chip chip-info',
  partially_paid: 'chip chip-warning',
  paid: 'chip chip-success',
  overdue: 'chip chip-danger',
};

const fmtINR = (n) => n == null ? '—' : `₹ ${Number(n).toLocaleString('en-IN')}`;

export default function FollowupsPage() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const validTabs = ['actions', 'approvals', 'collections'];
  const initialTab = validTabs.includes(searchParams.get('tab')) ? searchParams.get('tab') : 'actions';
  const [tab, setTabState] = useState(initialTab);
  const setTab = useCallback((next) => {
    setTabState(next);
    const params = new URLSearchParams(searchParams);
    if (next === 'actions') params.delete('tab');
    else params.set('tab', next);
    setSearchParams(params, { replace: true });
  }, [searchParams, setSearchParams]);
  const [rows, setRows] = useState([]);
  const [projectsMap, setProjectsMap] = useState({});
  const [summary, setSummary] = useState({
    my_open_actions: 0, overdue_actions: 0, overdue_approvals: 0,
    overdue_collections: 0, expected_this_month: 0, received_this_month: 0,
  });
  const [filters, setFilters] = useState({
    project_id: searchParams.get('project_id') || '',
    responsible: searchParams.get('responsible') || '',
    status: searchParams.get('status') || '',
    overdue: searchParams.get('overdue') === '1' ? 1 : 0,
  });
  const [users, setUsers] = useState([]);
  const [projectOptions, setProjectOptions] = useState([]);
  const [busy, setBusy] = useState(false);
  const [openLog, setOpenLog] = useState(null);       // {kind, row}

  const loadSummary = useCallback(async () => {
    try {
      const r = await api.get('/followups/summary');
      setSummary(r.data);
    } catch { /* soft */ }
  }, []);

  const loadRows = useCallback(async () => {
    setBusy(true);
    try {
      const params = new URLSearchParams({ tab });
      if (filters.project_id)  params.set('project_id', filters.project_id);
      if (filters.responsible) params.set('responsible', filters.responsible);
      if (filters.status)      params.set('status', filters.status);
      if (filters.overdue)     params.set('overdue', '1');
      const r = await api.get(`/followups?${params.toString()}`);
      setRows(r.data.rows || []);
      setProjectsMap(r.data.projects || {});
    } catch (e) {
      toast.error(parseErr(e));
    } finally { setBusy(false); }
  }, [tab, filters]);

  useEffect(() => {
    Promise.all([
      api.get('/users').catch(() => ({ data: [] })),
      api.get('/master-projects').catch(() => ({ data: { rows: [] } })),
    ]).then(([u, p]) => {
      setUsers(u.data || []);
      setProjectOptions(p.data.rows || []);
    });
    loadSummary();
  }, [loadSummary]);

  useEffect(() => { loadRows(); }, [loadRows]);

  // Keep tab in sync if URL changes externally (e.g. back button, drill-through link)
  useEffect(() => {
    const urlTab = searchParams.get('tab');
    if (validTabs.includes(urlTab) && urlTab !== tab) setTabState(urlTab);
    else if (!urlTab && tab !== 'actions') setTabState('actions');
  }, [searchParams]);

  useAutoRefetch(() => { loadSummary(); loadRows(); },
    { collections: ['actions', 'client_approvals', 'collections_ledger', 'master_projects'] });

  const usersById = useMemo(() => Object.fromEntries((users || []).map(u => [u.id, u])), [users]);

  const statusOptions = Object.entries(STATUS_LABELS[tab] || {});

  return (
    <AppShell breadcrumbs={[{ label: 'Follow-ups' }]}>
      <div className="p-6 max-w-[1400px] mx-auto" data-testid="followups-page">
        <div className="mb-4">
          <h1 className="font-heading text-2xl font-semibold">Follow-ups</h1>
          <p className="text-[13px] text-[hsl(var(--foreground-muted))]">
            Every open commitment, client response awaited, and payment in flight — in one place.
          </p>
        </div>

        {/* KPI strip */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-4" data-testid="followups-kpi">
          <KpiTile label="My open actions" value={summary.my_open_actions} icon={ClipboardList} tint="info" />
          <KpiTile label="Overdue actions" value={summary.overdue_actions} icon={AlertTriangle} tint="danger" />
          <KpiTile label="Overdue approvals" value={summary.overdue_approvals} icon={PhoneCall} tint="warning" />
          <KpiTile label="Overdue collections" value={summary.overdue_collections} icon={IndianRupee} tint="danger" />
          <KpiTile label="Expected this month" value={fmtINR(summary.expected_this_month)} icon={Clock} tint="info" />
          <KpiTile label="Received this month" value={fmtINR(summary.received_this_month)} icon={CheckCircle2} tint="success" />
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 mb-3 border-b" style={{ borderColor: 'hsl(var(--border))' }}>
          {TABS.map(t => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button key={t.id}
                      onClick={() => { setTab(t.id); setFilters(f => ({ ...f, status: '' })); }}
                      className={`inline-flex items-center gap-1.5 px-4 py-2 text-[13px] border-b-2 transition-colors ${
                        active
                          ? 'border-[hsl(var(--primary))] text-[hsl(var(--primary))] font-semibold'
                          : 'border-transparent text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--foreground))]'
                      }`}
                      data-testid={`followups-tab-${t.id}`}>
                <Icon size={14} />{t.label}
              </button>
            );
          })}
        </div>

        {/* Filters */}
        <div className="flex items-center flex-wrap gap-2 mb-3" data-testid="followups-filters">
          <Filter size={13} className="text-[hsl(var(--foreground-muted))]" />
          <select value={filters.project_id}
                  onChange={e => setFilters(f => ({ ...f, project_id: e.target.value }))}
                  className="h-8 border rounded-md px-2 text-[12px]"
                  style={{ borderColor: 'hsl(var(--border))' }}
                  data-testid="filter-project">
            <option value="">All projects</option>
            {projectOptions.map(p => (
              <option key={p.id} value={p.id}>{p.project_name || p.name} · {p.client}</option>
            ))}
          </select>
          <select value={filters.responsible}
                  onChange={e => setFilters(f => ({ ...f, responsible: e.target.value }))}
                  className="h-8 border rounded-md px-2 text-[12px]"
                  style={{ borderColor: 'hsl(var(--border))' }}
                  data-testid="filter-responsible">
            <option value="">Anyone</option>
            {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
          <select value={filters.status}
                  onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}
                  className="h-8 border rounded-md px-2 text-[12px]"
                  style={{ borderColor: 'hsl(var(--border))' }}
                  data-testid="filter-status">
            <option value="">Any status</option>
            {statusOptions.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
          <label className="inline-flex items-center gap-1 text-[12px] cursor-pointer">
            <input type="checkbox" checked={!!filters.overdue}
                   onChange={e => setFilters(f => ({ ...f, overdue: e.target.checked ? 1 : 0 }))}
                   data-testid="filter-overdue" />
            Overdue only
          </label>
          {(filters.project_id || filters.responsible || filters.status || filters.overdue) && (
            <button onClick={() => setFilters({ project_id: '', responsible: '', status: '', overdue: 0 })}
                    className="text-[11.5px] underline text-[hsl(var(--foreground-muted))]"
                    data-testid="filter-clear">Clear</button>
          )}
          {tab === 'collections' && (user?.role === 'owner' || user?.role === 'accounts') && (
            <div className="ml-auto">
              <BulkReconcileButton onDone={() => { loadSummary(); loadRows(); }} />
            </div>
          )}
        </div>

        {/* Table */}
        {busy ? (
          <div className="py-10 text-center text-[hsl(var(--foreground-muted))]">Loading…</div>
        ) : rows.length === 0 ? (
          <EmptyState tab={tab} />
        ) : (
          <div className="border rounded-xl bg-[hsl(var(--card))] overflow-x-auto"
               style={{ borderColor: 'hsl(var(--border))' }}>
            <TabTable
              tab={tab} rows={rows} projectsMap={projectsMap} usersById={usersById}
              user={user}
              onLog={(row) => setOpenLog({ kind: tab, row })}
              onChanged={() => { loadSummary(); loadRows(); }}
            />
          </div>
        )}

        {openLog && (
          <LogDialog {...openLog}
                     users={users}
                     onClose={() => setOpenLog(null)}
                     onDone={() => { setOpenLog(null); loadSummary(); loadRows(); }} />
        )}
      </div>
    </AppShell>
  );
}

function KpiTile({ label, value, icon: Icon, tint }) {
  const tintClass = {
    info: 'bg-[hsl(210_90%_96%)] text-[hsl(210_60%_30%)] border-[hsl(210_70%_86%)]',
    warning: 'bg-[hsl(48_96%_94%)] text-[hsl(38_60%_28%)] border-[hsl(48_80%_75%)]',
    danger: 'bg-[hsl(0_90%_96%)] text-[hsl(0_60%_35%)] border-[hsl(0_70%_82%)]',
    success: 'bg-[hsl(142_70%_94%)] text-[hsl(142_50%_25%)] border-[hsl(142_60%_78%)]',
  }[tint] || 'bg-[hsl(var(--surface-1))] border-[hsl(var(--border))]';
  return (
    <div className={`rounded-lg border p-3 ${tintClass}`}>
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide opacity-90">
        <Icon size={12} />
        <span>{label}</span>
      </div>
      <div className="text-[20px] font-semibold mt-1 tabular-nums truncate">{value}</div>
    </div>
  );
}

function EmptyState({ tab }) {
  const copy = {
    actions:     { title: 'No open actions match these filters.', body: 'Every commitment logged against a project appears here.' },
    approvals:   { title: 'No approvals in flight.',              body: 'Deliverables, quotes, and PIs awaiting a client response show up here.' },
    collections: { title: 'No collections to track.',             body: 'Proforma invoices, promised payments, and receivables live here.' },
  }[tab];
  return (
    <div className="rounded-lg border p-10 text-center" style={{ borderColor: 'hsl(var(--border))' }}
         data-testid={`followups-empty-${tab}`}>
      <div className="text-[13px] font-semibold">{copy.title}</div>
      <div className="text-[12px] text-[hsl(var(--foreground-muted))] mt-1">{copy.body}</div>
    </div>
  );
}

function TabTable({ tab, rows, projectsMap, usersById, user, onLog, onChanged }) {
  const isOwner = user?.role === 'owner';
  const projectCell = (pid) => {
    const p = projectsMap[pid];
    return (
      <div className="min-w-0">
        <a href={`/projects/${pid}`} className="font-medium text-[13px] hover:underline block truncate">
          {p?.project_name || pid.slice(0, 8)}
        </a>
        <div className="text-[11px] text-[hsl(var(--foreground-subtle))] truncate">
          {p?.client}{p?.code ? ` · ${p.code}` : ''}
        </div>
      </div>
    );
  };
  const userCell = (uid) => usersById[uid]?.name || <span className="text-[hsl(var(--foreground-subtle))]">—</span>;

  if (tab === 'actions') {
    return (
      <table className="tbl">
        <thead><tr>
          <th>Action</th><th>Project</th><th>Owner</th><th>Due</th>
          <th>Status</th><th>Last update</th><th>Blocker</th><th></th>
        </tr></thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.id}
                data-testid={`action-row-${r.id}`}
                data-overdue={r.is_overdue ? 'true' : 'false'}
                data-stale={r.is_stale ? 'true' : 'false'}
                style={r.is_overdue ? { boxShadow: 'inset 3px 0 0 hsl(var(--danger))' } :
                       r.is_stale ? { boxShadow: 'inset 3px 0 0 hsl(48 90% 55%)' } : undefined}>
              <td className="font-medium">{r.title}</td>
              <td>{projectCell(r.project_id)}</td>
              <td className="text-[12.5px]">{userCell(r.owner_id)}</td>
              <td className="text-[12px]">
                {r.due_date ? fmtDate(r.due_date) : <span className="text-[hsl(var(--foreground-subtle))]">—</span>}
                {r.is_overdue && <span className="chip chip-danger text-[10px] ml-1">OVERDUE</span>}
                {r.is_stale && !r.is_overdue && <span className="chip chip-warning text-[10px] ml-1">STALE</span>}
              </td>
              <td><span className={STATUS_TINT[r.status]}>{STATUS_LABELS.actions[r.status]}</span></td>
              <td className="text-[11.5px] text-[hsl(var(--foreground-muted))]">
                {r.last_update_at ? fmtDate(r.last_update_at) : '—'}
                {r.last_update_by && <div className="truncate max-w-[140px]">{r.last_update_by}</div>}
              </td>
              <td className="text-[11.5px] text-[hsl(var(--foreground-muted))] max-w-[160px] truncate">{r.blocker || '—'}</td>
              <td className="text-right">
                <Button size="sm" variant="outline" onClick={() => onLog(r)}
                        data-testid={`action-log-${r.id}`}>Log update</Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  if (tab === 'approvals') {
    return (
      <table className="tbl">
        <thead><tr>
          <th>Item</th><th>Project</th><th>Type</th><th>Amount</th>
          <th>Sent</th><th>Expected reply</th><th>Owner</th>
          <th>Status</th><th>Evidence</th><th></th>
        </tr></thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.id}
                data-testid={`approval-row-${r.id}`}
                data-overdue={r.is_overdue ? 'true' : 'false'}
                style={r.is_overdue ? { boxShadow: 'inset 3px 0 0 hsl(0 70% 55%)', background: 'hsl(0 90% 98%)' } : undefined}>
              <td className="font-medium">
                <div>{r.item_title}</div>
                <div className="text-[11px] text-[hsl(var(--foreground-subtle))]">{r.client_contact_name}</div>
              </td>
              <td>{projectCell(r.project_id)}</td>
              <td className="text-[12px]"><span className="chip">{r.item_type}</span></td>
              <td className="text-[12.5px] tabular-nums">{fmtINR(r.amount)}</td>
              <td className="text-[12px]">{r.sent_date ? fmtDate(r.sent_date) : '—'}</td>
              <td className="text-[12px]">
                {r.expected_response_date ? fmtDate(r.expected_response_date) : '—'}
                {r.is_overdue && <span className="chip chip-danger text-[10px] ml-1">OVERDUE</span>}
              </td>
              <td className="text-[12.5px]">{userCell(r.responsible_user_id)}</td>
              <td><span className={STATUS_TINT[r.status]}>{STATUS_LABELS.approvals[r.status]}</span></td>
              <td>
                <SupportingEvidence
                  entity="client-approvals" parentId={r.id} items={r.attachments}
                  canEdit={['owner', 'accounts', 'operations', 'dm_creative', 'dm_technical', 'dm_architecture'].includes(user?.role)}
                  currentUserEmail={user?.email} isOwner={isOwner}
                  onChange={onChanged}
                />
              </td>
              <td className="text-right">
                <Button size="sm" variant="outline" onClick={() => onLog(r)}
                        data-testid={`approval-log-${r.id}`}>Log response</Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  // collections
  return (
    <table className="tbl">
      <thead><tr>
        <th>PI</th><th>Project</th><th>Total</th><th>Received</th><th>Balance</th>
        <th>Due</th><th>Last / Next follow-up</th><th>Responsible</th>
        <th>Status</th><th>Evidence</th><th></th>
      </tr></thead>
      <tbody>
        {rows.map(r => (
          <tr key={r.id}
              data-testid={`collection-row-${r.id}`}
              data-overdue={r.is_overdue ? 'true' : 'false'}
              style={r.is_overdue ? { boxShadow: 'inset 3px 0 0 hsl(0 70% 55%)', background: 'hsl(0 90% 98%)' } : undefined}>
            <td className="font-medium font-mono text-[12px]">{r.pi_number}</td>
            <td>{projectCell(r.project_id)}</td>
            <td className="tabular-nums">{fmtINR(r.total_amount)}</td>
            <td className="tabular-nums text-[hsl(var(--success))]">{fmtINR(r.amount_received)}</td>
            <td className="tabular-nums font-semibold">{fmtINR(r.balance)}</td>
            <td className="text-[12px]">
              {r.due_date ? fmtDate(r.due_date) : '—'}
              {r.is_overdue && <span className="chip chip-danger text-[10px] ml-1">OVERDUE</span>}
            </td>
            <td className="text-[11.5px] text-[hsl(var(--foreground-muted))]">
              {r.last_followup_date && <div>Last: {fmtDate(r.last_followup_date)}</div>}
              {r.next_followup_date && <div>Next: {fmtDate(r.next_followup_date)}</div>}
            </td>
            <td className="text-[12.5px]">{userCell(r.responsible_accounts_user_id)}</td>
            <td><span className={STATUS_TINT[r.status]}>{STATUS_LABELS.collections[r.status]}</span></td>
            <td>
              <SupportingEvidence
                entity="collections" parentId={r.id} items={r.attachments}
                canEdit={['owner', 'accounts', 'dm_creative', 'dm_technical', 'dm_architecture'].includes(user?.role)}
                currentUserEmail={user?.email} isOwner={isOwner}
                onChange={onChanged}
              />
            </td>
            <td className="text-right whitespace-nowrap">
              <Button size="sm" variant="outline" onClick={() => onLog(r)}
                      data-testid={`collection-log-${r.id}`}>Log</Button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// -------- Log dialog (context-aware based on `kind`) --------

function LogDialog({ kind, row, users, onClose, onDone }) {
  const { user } = useAuth();
  const isOwner = user?.role === 'owner';
  const canReconcile = isOwner || user?.role === 'accounts';
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(kind === 'actions' ? 'in_progress' :
                                        kind === 'approvals' ? 'approved' : 'sent');
  const [note, setNote] = useState('');
  const [amount, setAmount] = useState('');
  const [receivedOn, setReceivedOn] = useState('');
  const [nextFollowup, setNextFollowup] = useState('');
  const [respMethod, setRespMethod] = useState('phone');
  const [respDate, setRespDate] = useState('');
  const [blocker, setBlocker] = useState('');

  const submit = async (variant) => {
    setBusy(true);
    try {
      if (kind === 'actions') {
        await api.post(`/actions/${row.id}/update`,
                       { status, note: note || undefined, blocker: status === 'blocked' ? blocker : undefined });
      } else if (kind === 'approvals') {
        await api.post(`/client-approvals/${row.id}/log-response`,
                       { status, response_method: respMethod,
                         response_date: respDate || undefined,
                         notes: note || undefined });
      } else if (kind === 'collections') {
        if (variant === 'payment') {
          if (!amount) return;
          await api.post(`/collections/${row.id}/record-payment`,
                         { amount: Number(amount),
                           received_on: receivedOn || undefined,
                           notes: note || undefined });
        } else if (variant === 'reconcile') {
          const r = await api.post(`/collections/${row.id}/reconcile`);
          if (r.data.no_op) toast.message('Already reconciled — no change.');
          else toast.success(`Bumped project paid_amount by ₹ ${Number(r.data.delta).toLocaleString('en-IN')}`);
          onDone(); return;
        } else {
          await api.post(`/collections/${row.id}/followup`,
                         { next_followup_date: nextFollowup || undefined,
                           notes: note || undefined });
        }
      }
      toast.success('Logged');
      onDone();
    } catch (e) { toast.error(parseErr(e)); }
    finally { setBusy(false); }
  };

  const title = {
    actions:     'Log an update',
    approvals:   'Log client response',
    collections: 'Follow-up on this PI',
  }[kind];

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-[440px]" data-testid={`log-dialog-${kind}`}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription className="truncate">
            {row.title || row.item_title || row.pi_number}
          </DialogDescription>
        </DialogHeader>

        {kind === 'actions' && (
          <div className="space-y-2">
            <label className="block">
              <span className="text-[12px] text-[hsl(var(--foreground-muted))]">Status</span>
              <select value={status} onChange={e => setStatus(e.target.value)}
                      className="mt-1 w-full h-9 border rounded-md px-2 text-[13px]"
                      style={{ borderColor: 'hsl(var(--border))' }}
                      data-testid="log-action-status">
                {Object.entries(STATUS_LABELS.actions).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
            </label>
            {status === 'blocked' && (
              <label className="block">
                <span className="text-[12px] text-[hsl(var(--foreground-muted))]">What&apos;s blocking?</span>
                <Input value={blocker} onChange={e => setBlocker(e.target.value)}
                       placeholder="Client approval, budget, external dependency…"
                       className="mt-1" data-testid="log-action-blocker" />
              </label>
            )}
            <label className="block">
              <span className="text-[12px] text-[hsl(var(--foreground-muted))]">Note (optional)</span>
              <Input value={note} onChange={e => setNote(e.target.value)}
                     placeholder="Progress, next steps, context…"
                     className="mt-1" data-testid="log-action-note" />
            </label>
          </div>
        )}

        {kind === 'approvals' && (
          <div className="space-y-2">
            <label className="block">
              <span className="text-[12px] text-[hsl(var(--foreground-muted))]">Client response</span>
              <select value={status} onChange={e => setStatus(e.target.value)}
                      className="mt-1 w-full h-9 border rounded-md px-2 text-[13px]"
                      style={{ borderColor: 'hsl(var(--border))' }}
                      data-testid="log-approval-status">
                {Object.entries(STATUS_LABELS.approvals).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
            </label>
            <div className="grid grid-cols-2 gap-2">
              <label className="block">
                <span className="text-[12px] text-[hsl(var(--foreground-muted))]">Via</span>
                <select value={respMethod} onChange={e => setRespMethod(e.target.value)}
                        className="mt-1 w-full h-9 border rounded-md px-2 text-[13px]"
                        style={{ borderColor: 'hsl(var(--border))' }}
                        data-testid="log-approval-method">
                  <option value="phone">Phone</option>
                  <option value="email">Email</option>
                  <option value="in_person">In person</option>
                  <option value="whatsapp">WhatsApp</option>
                  <option value="other">Other</option>
                </select>
              </label>
              <label className="block">
                <span className="text-[12px] text-[hsl(var(--foreground-muted))]">Response date</span>
                <Input type="date" value={respDate} onChange={e => setRespDate(e.target.value)}
                       className="mt-1" data-testid="log-approval-date" />
              </label>
            </div>
            <label className="block">
              <span className="text-[12px] text-[hsl(var(--foreground-muted))]">Notes</span>
              <Input value={note} onChange={e => setNote(e.target.value)}
                     placeholder="What did they say?"
                     className="mt-1" data-testid="log-approval-notes" />
            </label>
          </div>
        )}

        {kind === 'collections' && (
          <div className="space-y-3">
            <div className="text-[12px] text-[hsl(var(--foreground-muted))]">
              Outstanding: <strong className="tabular-nums">{fmtINR(row.balance)}</strong> ·
              Total: <strong className="tabular-nums">{fmtINR(row.total_amount)}</strong>
            </div>
            <div className="border rounded-lg p-3 space-y-2" style={{ borderColor: 'hsl(var(--border))' }}>
              <div className="text-[12px] font-semibold flex items-center gap-1"><IndianRupee size={12} />Record a payment</div>
              <div className="grid grid-cols-2 gap-2">
                <Input type="number" value={amount} onChange={e => setAmount(e.target.value)}
                       placeholder="Amount" data-testid="log-payment-amount" />
                <Input type="date" value={receivedOn} onChange={e => setReceivedOn(e.target.value)}
                       data-testid="log-payment-date" />
              </div>
              <Input value={note} onChange={e => setNote(e.target.value)}
                     placeholder="Notes (optional)" data-testid="log-payment-notes" />
              <Button size="sm" onClick={() => submit('payment')} disabled={busy || !amount}
                      data-testid="log-payment-submit" className="w-full">Record payment</Button>
            </div>
            <div className="border rounded-lg p-3 space-y-2" style={{ borderColor: 'hsl(var(--border))' }}>
              <div className="text-[12px] font-semibold flex items-center gap-1"><PhoneCall size={12} />Log follow-up call</div>
              <div className="grid grid-cols-2 gap-2 items-center">
                <span className="text-[11px] text-[hsl(var(--foreground-muted))]">Next follow-up</span>
                <Input type="date" value={nextFollowup} onChange={e => setNextFollowup(e.target.value)}
                       data-testid="log-followup-next" />
              </div>
              <Button size="sm" variant="outline" onClick={() => submit('followup')} disabled={busy}
                      data-testid="log-followup-submit" className="w-full">Save follow-up</Button>
            </div>
            {canReconcile && (
              <div className="border rounded-lg p-3 space-y-2 border-dashed"
                   style={{ borderColor: 'hsl(var(--border))' }}>
                <div className="text-[12px] font-semibold flex items-center gap-1"><CheckCircle2 size={12} />Reconcile to project</div>
                <div className="text-[11.5px] text-[hsl(var(--foreground-muted))]">
                  Bumps the Master Project Data <em>paid_amount</em> by the unreconciled portion of this collection. Idempotent — safe to click any time.
                </div>
                <Button size="sm" onClick={() => submit('reconcile')} disabled={busy}
                        data-testid="log-reconcile-submit" className="w-full">Reconcile now</Button>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={busy}>Close</Button>
          {kind !== 'collections' && (
            <Button onClick={() => submit()} disabled={busy} data-testid={`log-${kind}-submit`}>
              {busy ? 'Saving…' : 'Save'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function BulkReconcileButton({ onDone }) {
  const [busy, setBusy] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [preview, setPreview] = useState(null);

  const loadPreview = async () => {
    setBusy(true);
    try {
      const r = await api.get('/reports/kra/collections/cleared-preview');
      setPreview(r.data);
      setPreviewOpen(true);
    } catch (e) {
      toast.error(parseErr(e));
    } finally { setBusy(false); }
  };

  const confirm = async () => {
    setBusy(true);
    try {
      const r = await api.post('/reports/kra/collections/reconcile-cleared', {});
      const n = r.data?.reconciled?.length || 0;
      const delta = r.data?.total_delta || 0;
      const skipped = r.data?.skipped?.length || 0;
      if (n === 0) {
        toast.message('Nothing to reconcile — everything is already up to date.');
      } else {
        toast.success(
          `Reconciled ${n} PI${n === 1 ? '' : 's'} · ₹ ${Number(delta).toLocaleString('en-IN')} pushed to Master Projects` +
          (skipped ? ` (${skipped} skipped)` : ''),
        );
      }
      setPreviewOpen(false);
      onDone && onDone();
    } catch (e) { toast.error(parseErr(e)); }
    finally { setBusy(false); }
  };

  return (
    <>
      <Button size="sm" variant="outline" onClick={loadPreview} disabled={busy}
              data-testid="collections-bulk-reconcile">
        <CheckCircle2 size={13} className="mr-1" />
        Reconcile cleared PIs
      </Button>

      {previewOpen && (
        <Dialog open onOpenChange={(v) => !v && setPreviewOpen(false)}>
          <DialogContent className="max-w-[560px]" data-testid="bulk-reconcile-dialog">
            <DialogHeader>
              <DialogTitle>Reconcile cleared PIs</DialogTitle>
              <DialogDescription>
                Every PI below is fully paid or has zero balance but its payment hasn&apos;t been pushed
                to the Master Project yet. Confirming will bump each project&apos;s <em>paid_amount</em> by
                the delta shown. Safe to run again later — already-reconciled rows are skipped.
              </DialogDescription>
            </DialogHeader>
            {preview?.count === 0 ? (
              <div className="py-6 text-center text-[hsl(var(--foreground-muted))]">
                🎉 Nothing pending. All cleared PIs are already reconciled.
              </div>
            ) : (
              <div className="max-h-[300px] overflow-y-auto border rounded-lg"
                   style={{ borderColor: 'hsl(var(--border))' }}
                   data-testid="bulk-reconcile-list">
                <table className="tbl">
                  <thead><tr><th>PI</th><th>Project</th><th>Delta (₹)</th></tr></thead>
                  <tbody>
                    {preview.rows.map(r => (
                      <tr key={r.id} data-testid={`bulk-reconcile-row-${r.id}`}>
                        <td className="font-mono text-[11.5px]">{r.pi_number}</td>
                        <td className="truncate max-w-[240px]">{r.project_name}</td>
                        <td className="tabular-nums font-semibold text-[hsl(142_50%_35%)]">
                          {fmtINR(r.delta)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {preview?.count > 0 && (
              <div className="text-[12px] text-right pt-1">
                Total to reconcile: <strong className="tabular-nums">{fmtINR(preview.total_delta)}</strong>
              </div>
            )}
            <DialogFooter>
              <Button variant="ghost" onClick={() => setPreviewOpen(false)} disabled={busy}>
                Cancel
              </Button>
              <Button onClick={confirm} disabled={busy || (preview?.count || 0) === 0}
                      data-testid="bulk-reconcile-confirm">
                {busy ? 'Reconciling…' : `Reconcile ${preview?.count || 0}`}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
